import React from 'react'
import { useTranslation } from 'react-i18next'
import { Znak } from '../oferta/ElementyRaportu'
import { etykietaWarunku, jednostkaWarunku } from './tlumaczenieWarunkow'

/**
 * Warunek doboru jako pasek: dozwolony zakres i wartość wypadkowa na jednej osi.
 *
 * DLACZEGO PASEK, A NIE „611 V z 1000 V": sama para liczb nie mówi, czy jest ciasno.
 * 611 przy limicie 1000 to spory zapas, 980 przy 1000 to ledwo — z tekstu obie
 * wyglądają tak samo, na osi różnica widać natychmiast. Instalator czyta z tego
 * jedną rzecz: ile zostało miejsca, zanim dobór przestanie działać.
 *
 * OŚ ZACZYNA SIĘ OD ZERA, gdy warunek ma tylko górną granicę (napięcie maksymalne —
 * każda wartość niższa jest dobra), a od okolic dolnej granicy, gdy zakres jest
 * obustronny (okno MPPT, DC/AC). Inaczej przy zakresie 1,00–1,30 cała skala od zera
 * zgniotłaby różnicę do niewidocznego paska.
 *
 * WYGLĄD (przebudowa 2026-09-30): ten sam język co raport „Sprawdź ofertę”. Znacznik
 * statusu przed nazwą i wartość po prawej w jednym wierszu, pod nimi szary tor,
 * niebieski dozwolony zakres i kropka wartości w kolorze wyniku (jak pasek ceny
 * i skala mocy falownika w raporcie).
 */

const proc = (v, start, koniec) => ((v - start) / (koniec - start)) * 100

/** Etykieta przy brzegu paska nie może z niego wystawać. */
const przyBrzegu = (p) => {
  if (p <= 8) return { left: '0%', transform: 'none' }
  if (p >= 92) return { left: '100%', transform: 'translateX(-100%)' }
  return { left: `${p}%`, transform: 'translateX(-50%)' }
}

const PasekWarunku = ({
  etykieta: etykietaPl, wartosc, dolna, gorna, jednostka: jednostkaPl = '', format, spelniony: zZewnatrz,
}) => {
  const { t } = useTranslation('elektryka')
  // Etykieta i jednostka przychodzą z `lib/warunkiDoboru` po polsku (patrz tlumaczenieWarunkow).
  const etykieta = etykietaWarunku(t, etykietaPl)
  const jednostka = jednostkaWarunku(t, jednostkaPl)
  const pokaz = format || ((v) => `${String(v).replace('.', ',')}${jednostka ? ` ${jednostka}` : ''}`)

  const punkty = [wartosc, dolna, gorna].filter((v) => v != null)
  const vmin = Math.min(...punkty)
  const vmax = Math.max(...punkty)
  const margines = (vmax - vmin) * 0.18 || Math.abs(vmax) * 0.1 || 1

  const osStart = dolna == null ? 0 : Math.max(0, vmin - margines)
  const osKoniec = vmax + margines

  // Ocena przychodzi z `lib/warunkiDoboru` — ta sama, którą widzi panel w asystencie.
  // Własne liczenie zostaje wyłącznie na wypadek użycia paska poza tamtą listą.
  const spelniony = zZewnatrz != null
    ? zZewnatrz
    : (dolna == null || wartosc >= dolna) && (gorna == null || wartosc <= gorna)

  const dozwolonyOd = proc(dolna != null ? dolna : osStart, osStart, osKoniec)
  const dozwolonyDo = proc(gorna != null ? gorna : osKoniec, osStart, osKoniec)
  const pozycja = Math.min(100, Math.max(0, proc(wartosc, osStart, osKoniec)))

  return (
    <div className="py-2.5">
      <div className="flex items-center gap-2.5">
        <Znak rodzaj={spelniony ? 'tak' : 'sprawdz'} rozmiar="sm" />
        <span className="min-w-0 flex-1 text-sm leading-5 text-gray-700">{etykieta}</span>
        <span className={`text-sm font-semibold tabular-nums ${spelniony ? 'text-emerald-700' : 'text-amber-700'}`}>
          {pokaz(wartosc)}
        </span>
      </div>

      {/* Tor: szary poza zakresem, niebieski dozwolony zakres, kropka to wartość.
          Kolory wpisane klasami o stałych nazwach, bo Tailwind nie zbuduje klasy
          sklejanej w locie. */}
      <div className="relative ml-[30px] mt-2 h-3">
        <div className="absolute inset-x-0 top-0.5 h-2 rounded-full bg-gray-100 shadow-inner" />
        <div className="absolute top-0.5 h-2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600"
             style={{ left: `${dozwolonyOd}%`, width: `${dozwolonyDo - dozwolonyOd}%` }} />
        <div className={`absolute top-0 h-3 w-3 -translate-x-1/2 rounded-full border-[3px] bg-white shadow
                         ${spelniony ? 'border-emerald-600' : 'border-amber-500'}`}
             style={{ left: `${pozycja}%` }} />
      </div>

      {/* Granice najdrobniejszym drukiem, bo to kontekst, a nie odpowiedź */}
      <div className="relative ml-[30px] mt-1 h-4 text-[10px] tabular-nums text-gray-400">
        {dolna != null && (
          <span className="absolute whitespace-nowrap" style={przyBrzegu(dozwolonyOd)}>{pokaz(dolna)}</span>
        )}
        {gorna != null && (
          <span className="absolute whitespace-nowrap" style={przyBrzegu(dozwolonyDo)}>
            {dolna == null ? t('warunki.maks', { wartosc: pokaz(gorna) }) : pokaz(gorna)}
          </span>
        )}
      </div>
    </div>
  )
}

export default PasekWarunku
